/**
 * Centralized pricing configuration for Kura Stash.
 * 
 * New billing model:
 *   - paid_item_slots = items user pays for
 *   - free_items = base grant (20) + promo grants (adds capacity, not discount)
 *   - total_capacity = paid_item_slots + free_items
 *   - monthly_cost = paid_item_slots x tier_rate (flat per-tier pricing)
 *   - Tier auto-upgrades when paid_item_slots crosses threshold
 * 
 * Usage:
 *   import { usePricing } from '../config/pricing';
 *   const { plans, tierRates, crossovers, loading } = usePricing();
 */

import { useState, useEffect } from 'react';

import { BACKEND_URL } from '../config/api';

// Tier rate boundaries (paid_item_slots → rate)
const DEFAULT_TIER_RATES = [
  { tier: 'starter', name: 'Starter', min_items: 100, max_items: 199, price_per_item: 0.0299 },
  { tier: 'crafter', name: 'Crafter', min_items: 200, max_items: 499, price_per_item: 0.02495 },
  { tier: 'artisan', name: 'Artisan', min_items: 500, max_items: 99999, price_per_item: 0.01998 },
];

const DEFAULT_FREE_TIER = {
  tier_id: 'free',
  name: 'Free',
  item_limit: 20,
  price_monthly: 0,
  price_per_item: 0,
  features: ['20 free items (grant)', 'Craft Companion', 'Search by color & sentiment', 'Snap a Photo & URL entry'],
};

const DEFAULT_PACKAGES = {
  starter: {
    tier_id: 'starter', name: 'Starter', item_limit: 100, price_monthly: 2.99, price_per_item: 0.0299,
    features: ['100 paid item slots', 'Craft Companion', 'Search by color & sentiment', 'Snap a Photo & URL entry'],
  },
  crafter: {
    tier_id: 'crafter', name: 'Crafter', item_limit: 200, price_monthly: 4.99, price_per_item: 0.02495,
    features: ['200 paid item slots', 'All Starter features', '17% savings per item'],
    popular: true,
  },
  artisan: {
    tier_id: 'artisan', name: 'Artisan', item_limit: 500, price_monthly: 9.99, price_per_item: 0.01998,
    features: ['500 paid item slots', 'All Crafter features', '33% savings per item', 'Best value'],
  },
};

const DEFAULT_ADD_ON_INCREMENTS = [10, 50, 100, 500];
const DEFAULT_FREE_BASE_GRANT = 20;

let cachedPricing = null;
let fetchPromise = null;

/** Get tier info for a given paid item count */
export const getTierForPaidItems = (paidItems, tierRates = DEFAULT_TIER_RATES) => {
  if (paidItems <= 0) return { tier: 'free', name: 'Free', price_per_item: 0 };
  for (const rate of tierRates) {
    if (paidItems >= rate.min_items && paidItems <= rate.max_items) return rate;
  }
  return tierRates[tierRates.length - 1];
};

/** Calculate monthly cost for a paid item count */
export const calculateMonthlyCost = (paidItems, tierRates = DEFAULT_TIER_RATES) => {
  if (paidItems <= 0) return 0;
  const tier = getTierForPaidItems(paidItems, tierRates);
  return Math.round(paidItems * tier.price_per_item * 100) / 100;
};

/** Calculate total capacity */
export const calculateTotalCapacity = (paidItems, freeItems = DEFAULT_FREE_BASE_GRANT) => {
  return paidItems + freeItems;
};

export const fetchPricing = async () => {
  if (cachedPricing) return cachedPricing;
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/billing/plans`);
      if (!response.ok) throw new Error('Failed to fetch pricing');
      const data = await response.clone().json();

      cachedPricing = {
        freeTier: { tier_id: 'free', ...data.free },
        packages: Object.entries(data.packages || {}).reduce((acc, [id, pkg]) => {
          acc[id] = { tier_id: id, ...pkg, popular: id === 'crafter' };
          return acc;
        }, {}),
        tierRates: data.tier_rates || DEFAULT_TIER_RATES,
        crossovers: data.crossovers || [],
        addOnIncrements: data.add_on_increments || DEFAULT_ADD_ON_INCREMENTS,
        freeBaseGrant: data.free_base_grant || DEFAULT_FREE_BASE_GRANT,
      };
      return cachedPricing;
    } catch (error) {
      console.warn('Failed to fetch pricing, using defaults:', error);
      cachedPricing = {
        freeTier: DEFAULT_FREE_TIER,
        packages: DEFAULT_PACKAGES,
        tierRates: DEFAULT_TIER_RATES,
        crossovers: [],
        addOnIncrements: DEFAULT_ADD_ON_INCREMENTS,
        freeBaseGrant: DEFAULT_FREE_BASE_GRANT,
      };
      return cachedPricing;
    } finally {
      fetchPromise = null;
    }
  })();
  return fetchPromise;
};

export const getPricingSync = () => {
  if (cachedPricing) return cachedPricing;
  fetchPricing();
  return {
    freeTier: DEFAULT_FREE_TIER,
    packages: DEFAULT_PACKAGES,
    tierRates: DEFAULT_TIER_RATES,
    crossovers: [],
    addOnIncrements: DEFAULT_ADD_ON_INCREMENTS,
    freeBaseGrant: DEFAULT_FREE_BASE_GRANT,
  };
};

export const getAllPlansArray = (pricing) => {
  const { freeTier, packages } = pricing || getPricingSync();
  return [freeTier, ...Object.values(packages)].sort((a, b) => a.item_limit - b.item_limit);
};

export const getPaidPlansArray = (pricing) => {
  const { packages } = pricing || getPricingSync();
  return Object.values(packages).sort((a, b) => a.item_limit - b.item_limit);
};

export const usePricing = () => {
  const [pricing, setPricing] = useState(getPricingSync());
  const [loading, setLoading] = useState(!cachedPricing);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchPricing()
      .then(data => { if (mounted) { setPricing(data); setLoading(false); } })
      .catch(err => { if (mounted) { setError(err); setLoading(false); } });
    return () => { mounted = false; };
  }, []);

  return {
    ...pricing,
    plans: getAllPlansArray(pricing),
    paidPlans: getPaidPlansArray(pricing),
    loading,
    error,
  };
};

export const clearPricingCache = () => {
  cachedPricing = null;
  fetchPromise = null;
};

export default { usePricing, fetchPricing, getPricingSync, getAllPlansArray, getPaidPlansArray, clearPricingCache, getTierForPaidItems, calculateMonthlyCost, calculateTotalCapacity };
